import {Alert, FlexColumn, Heading, Separator, Table, IconTooltip} from '@forecast-it/design-system';
import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import styled from 'styled-components';
import {currencies} from '../../../constants';
import Util from '../../shared/util/util';

const AlertContainer = styled.div`
	margin: 16px 0px;
`;

const HeadingContainer = styled.div`
	margin: 12px 0px;
`;

interface Company {
	id: string;
	currency: string;
}

interface ExchangeRate {
	id: string;
	currency: string;
	rate: number | null;
	isUsed: boolean | null;
	createdAt: string | null;
}

interface ExchangeRateRow extends ExchangeRate {
	currencyCodeSymbol: string;
	currencyName: string;
	ratePrimary: string;
	rateSecondary: string;
	createdAtFormatted: string;
}

const parseCurrencyLabel = (label: string): {name: string; codeSymbol: string} => {
	const separatorIndex = label.indexOf(' - ');
	if (separatorIndex === -1) {
		return {name: label, codeSymbol: label};
	}

	return {
		name: label.substring(0, separatorIndex),
		codeSymbol: label.substring(separatorIndex + 3),
	};
};

export const Currencies = ({company, exchangeRates}: {company: Company; exchangeRates: ExchangeRate[]}) => {
	const {formatMessage} = useIntl();
	const [isDismissed, setIsDismissed] = useState(false);
	const [data, setData] = useState<ExchangeRateRow[]>([]);

	useEffect(() => {
		setData(
			exchangeRates.map(exchangeRate => {
				const currencyEntry = currencies.find(c => c.value === exchangeRate.currency);
				const {name: currencyName, codeSymbol: currencyCodeSymbol} = parseCurrencyLabel(
					currencyEntry?.label ?? exchangeRate.currency
				);
				const rate = exchangeRate.rate ?? 0;

				return {
					...exchangeRate,
					currencyCodeSymbol,
					currencyName,
					ratePrimary: `${rate} ${exchangeRate.currency}`,
					rateSecondary: `1 ${company.currency} = ${rate} ${exchangeRate.currency}`,
					createdAtFormatted: Util.formatUtcDateTimeToLocalTimezone(exchangeRate.createdAt),
				};
			})
		);
	}, [exchangeRates, company]);

	return (
		<FlexColumn>
			{!isDismissed && (
				<AlertContainer>
					<Alert
						text={formatMessage({id: 'settings_finance.currencies.info_banner'})}
						type={Alert.TYPE.INFORMATION}
						onDismiss={() => setIsDismissed(true)}
					/>
				</AlertContainer>
			)}

			<HeadingContainer>
				<FlexColumn gap="l">
					<Heading size={4}>{formatMessage({id: 'settings_finance.currencies.page_title'})}</Heading>
					<Separator />
				</FlexColumn>
			</HeadingContainer>

			<Table width={'100%'} data={data}>
				<Table.TitleSubtitleColumn
					accessorKey="currencyCodeSymbol"
					header={formatMessage({id: 'common.currency'})}
					allowSorting
					renderTitle={(row: ExchangeRateRow) => row.currencyCodeSymbol}
					renderSubtitle={(row: ExchangeRateRow) => row.currencyName}
					renderTitleAccessory={(row: ExchangeRateRow) =>
						row.isUsed ? (
							<IconTooltip icon="lockClosed" iconColor="onlight">
								<IconTooltip.Item
									details={formatMessage({id: 'settings_finance.currencies.locked_tooltip.description'})}
								/>
							</IconTooltip>
						) : null
					}
				/>
				<Table.TitleSubtitleColumn
					accessorKey="ratePrimary"
					header={formatMessage({id: 'settings_finance.currencies.table.currency_rate'})}
					allowSorting
					renderTitle={(row: ExchangeRateRow) => row.ratePrimary}
					renderSubtitle={(row: ExchangeRateRow) => row.rateSecondary}
				/>
				<Table.TextColumn
					accessorKey="createdAtFormatted"
					header={formatMessage({id: 'global_filter_field.created_at'})}
					allowSorting
					sortingFn="text"
				/>
				<Table.ActionColumn header={formatMessage({id: 'common.actions'})} hideHeader>
					<Table.ActionColumn.Option onClick={() => undefined}>
						<FormattedMessage id="settings_finance.currencies.table.view_add_rate" />
					</Table.ActionColumn.Option>
					<Table.ActionColumn.Option onClick={() => undefined}>
						<FormattedMessage id="common.delete" />
					</Table.ActionColumn.Option>
				</Table.ActionColumn>
			</Table>
		</FlexColumn>
	);
};
